import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { BordOneToneIcon, NudosSearchBar } from 'components/NudosComponents';
import CustomSelect from 'components/customSelect';
import useSWR from 'swr';
import useDebounce from 'hooks/useDebounce';
import useFiltersState from 'state/useFiltersState';
import { IProductList, ORDER_PROPERTIES_TO_SEARCH, ORDER_STATUS_ID } from 'types/orders';
import OrderCancelledCard from '../../../../../components/CardInOrderList/OrderCancelledCard/OrderCancelledCard';
import { nudosGray100 } from 'styles/styles.constants';
import { getOrdersByStatus } from 'services/orders';
import { mockCancelledOrders } from '../../OrdersListingFinances/ordersFinancesMocks.temp';
import LoaderTrelloCard from 'views/shopping/components/LoaderTrelloCard/LoaderTrelloCard';
import UnsuccessfulSearch from 'components/unsuccessfulSearch/UnsuccessfulSearch';
import { optionsParamsSwr } from 'utils/optionsParamsSwr';
import './OrdersCancelledModalContent.scss';

/**
 * Componente que muestra el contenido del modal de órdenes canceladas.
 * Incluye una barra de búsqueda con selector de tipo de búsqueda y lista de órdenes canceladas.
 * Las búsquedas en este modal afectan al endpoint de cancelledOrders y también usa la búsqueda global.
 */
const OrdersCancelledModalContent = ({ handleClose }: IOrdersCancelledModalContentProps) => {
  const { ordersSearchbarText, propertyToFilterSearch: globalPropertyToFilterSearch } = useFiltersState();
  const [localSearchText, setLocalSearchText] = useState('');
  const [localPropertyToFilterSearch, setLocalPropertyToFilterSearch] = useState<{
    name: ORDER_PROPERTIES_TO_SEARCH;
    propertyToFilter: string;
  }>({
    name: ORDER_PROPERTIES_TO_SEARCH.ORDER_NUMBER,
    propertyToFilter: 'ORDER_NUMBER'
  });
  const [visibleCountCancelled, setVisibleCountCancelled] = useState(10);
  const [isLoadingMoreCancelled, setIsLoadingMoreCancelled] = useState(false);
  const containerListRef = useRef<HTMLDivElement>(null);

  const debounceDuration = 1500;
  const debouncedGlobalSearchText = useDebounce(ordersSearchbarText, debounceDuration);
  const debouncedLocalSearchText = useDebounce(localSearchText, debounceDuration);

  const effectiveSearchText = debouncedLocalSearchText || debouncedGlobalSearchText || '';
  const effectivePropertyToFilterSearch = localPropertyToFilterSearch?.propertyToFilter
    ? localPropertyToFilterSearch
    : globalPropertyToFilterSearch;

  const orderPropertiesToSearchValues = Object.values(ORDER_PROPERTIES_TO_SEARCH);
  const orderPropertiesToSearchKeys = Object.keys(ORDER_PROPERTIES_TO_SEARCH);

  const handleSetPropertyToFilterSearch = (value: ORDER_PROPERTIES_TO_SEARCH) =>
    setLocalPropertyToFilterSearch({
      name: value,
      propertyToFilter:
        orderPropertiesToSearchKeys?.find(
          key => ORDER_PROPERTIES_TO_SEARCH[key as keyof typeof ORDER_PROPERTIES_TO_SEARCH] === value
        ) || ''
    });

  const searchQueryParam = useMemo(() => {
    if (!effectiveSearchText || !effectivePropertyToFilterSearch?.propertyToFilter) return '';
    const searchObject = encodeURIComponent(
      JSON.stringify({
        name: effectiveSearchText.trim(),
        propertyToFilter: effectivePropertyToFilterSearch?.propertyToFilter
      })
    );
    return `search=${searchObject}`;
  }, [effectiveSearchText, effectivePropertyToFilterSearch?.propertyToFilter]);

  const prevQueryParamsRef = useRef<string>('');
  const queryParams = useMemo(() => {
    if (!searchQueryParam || searchQueryParam.length === 0) {
      const result = '';
      if (prevQueryParamsRef.current === result) return prevQueryParamsRef.current;
      prevQueryParamsRef.current = result;
      return result;
    }

    const result = `?${searchQueryParam}`;
    if (prevQueryParamsRef.current === result) return prevQueryParamsRef.current;
    prevQueryParamsRef.current = result;
    return result;
  }, [searchQueryParam]);

  const swrKeyCancelled = useMemo(
    () => [`orders-cancelled-modal`, ORDER_STATUS_ID.CANCELLED, queryParams],
    [queryParams]
  );

  const {
    data: cancelledOrders = [],
    isLoading,
    mutate
  } = useSWR(
    swrKeyCancelled,
    () => getOrdersByStatus(ORDER_STATUS_ID.CANCELLED, queryParams),
    optionsParamsSwr({
      errorRetryCount: 0,
      suspense: false
    })
  );

  const handleRefreshData = async () => {
    await mutate();
  };

  const formatOrders = (ordersList: IProductList[]) => {
    return ordersList.map(order => {
      order.id = JSON.stringify(order.orderId);
      return order;
    });
  };

  const formattedCancelledOrders = useMemo(() => {
    const formatted = formatOrders(cancelledOrders);
    return formatted.length > 0 ? formatted : formatOrders(mockCancelledOrders);
  }, [cancelledOrders]);

  const listOrdersCancelled = useMemo(
    () => formattedCancelledOrders?.slice(0, visibleCountCancelled) ?? [],
    [formattedCancelledOrders, visibleCountCancelled]
  );

  const handleLoadMoreCancelled = useCallback(() => {
    if (isLoadingMoreCancelled) return;
    setIsLoadingMoreCancelled(true);
    setTimeout(() => {
      setVisibleCountCancelled(prev => prev + 10);
      setIsLoadingMoreCancelled(false);
    }, 1000);
  }, [isLoadingMoreCancelled]);

  const hasMoreCancelled = visibleCountCancelled < formattedCancelledOrders?.length;

  const handleScroll = useCallback(() => {
    if (!containerListRef.current || !hasMoreCancelled || isLoadingMoreCancelled) return;
    const { scrollTop, scrollHeight, clientHeight } = containerListRef.current;
    const threshold = 100;
    const isNearBottom = (scrollHeight ?? 0) - (scrollTop ?? 0) - (clientHeight ?? 0) < threshold;
    if (isNearBottom) {
      handleLoadMoreCancelled();
    }
  }, [hasMoreCancelled, isLoadingMoreCancelled, handleLoadMoreCancelled]);

  const setupScrollListeners = useCallback(() => {
    const contentElement = containerListRef.current;
    if (!contentElement) return;

    contentElement.addEventListener('scroll', handleScroll);
    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    return setupScrollListeners();
  }, [setupScrollListeners]);

  useEffect(() => {
    setVisibleCountCancelled(10);
  }, [queryParams]);

  const getSearchbarPlaceholder = () => {
    switch (effectivePropertyToFilterSearch?.name) {
      case ORDER_PROPERTIES_TO_SEARCH.ORGANIZATION_NAME:
        return 'Escribe el nombre de empresa';
      case ORDER_PROPERTIES_TO_SEARCH.SERIAL_NUMBER:
        return 'Escribe el número del serial';
      case ORDER_PROPERTIES_TO_SEARCH.EMPLOYEE_NAME:
        return 'Escribe el nombre del empleado';
      case ORDER_PROPERTIES_TO_SEARCH.PURCHASE_ORDER:
        return 'Escribe el número de la orden de compra (PO)';
      default:
        return 'Escribe el número de orden';
    }
  };

  return (
    <div className="ordersCancelledModalContent">
      <div className="header">
        <div className="headerLeft">
          <div className="headerTitle">Órdenes canceladas</div>
        </div>
        <div className="headerRight">
          <div className="headerClose" onClick={handleClose}>
            <BordOneToneIcon variant="x" standardSize={12} />
          </div>
        </div>
      </div>
      <div className="containerContent">
        <div className="containerSearchBar">
          <NudosSearchBar
            customClassName="searchBarOrdersCancelled"
            customInputClass="searchBarOrdersCancelledInput"
            placeholder={getSearchbarPlaceholder()}
            handleChange={text => setLocalSearchText(text)}
            defaultValue={(localSearchText || ordersSearchbarText) ?? ''}
          />
          <CustomSelect
            customClassName="searchBarOrdersCancelledSelect"
            containerOptionsClassName="containerOptionsOrdersCancelledSelect"
            dropdownLabel="Tipo de búsqueda"
            items={orderPropertiesToSearchValues}
            value={effectivePropertyToFilterSearch?.name}
            onChangeSelect={value => handleSetPropertyToFilterSearch(value as ORDER_PROPERTIES_TO_SEARCH)}
            customBackground={nudosGray100}
            customBackgroundEmpty={nudosGray100}
          />
        </div>
        <div
          ref={containerListRef}
          className={`containerOrdersCancelledList ${listOrdersCancelled?.length === 0 ? 'withOutPadding' : ''}`}
          onScroll={handleScroll}
        >
          {isLoading ? (
            Array.from({ length: 10 }).map((_, index) => <LoaderTrelloCard key={`loader-cancelled-${index}`} />)
          ) : listOrdersCancelled?.length === 0 ? (
            <UnsuccessfulSearch
              customClassName="unsuccessfulSearchOrdersCancelled"
              customTextClassName="unsuccessfulSearchOrdersCancelledText"
              customText="No hay resultados para tu búsqueda, revisa el número de orden o prueba con otro tipo de búsqueda"
            />
          ) : (
            listOrdersCancelled?.map((order, index) => (
              <OrderCancelledCard
                key={`order-cancelled-${order.orderId}-${index}`}
                data={order}
                refreshData={handleRefreshData}
              />
            ))
          )}
          {isLoadingMoreCancelled && <LoaderTrelloCard key="loader-more-cancelled" />}
        </div>
      </div>
    </div>
  );
};

interface IOrdersCancelledModalContentProps {
  handleClose: () => void;
}

export default OrdersCancelledModalContent;
